// Sprint 17 — single in-flight indicator below the last message.
//
// Renders one of two visible kinds, never both:
//   - thinking → ••• Thinking · 0:08   (three pulsing dots + timer)
//   - tool     → 🔧 Working · 0:14 · <headline>
//
// Layered position: tail of the message list, mutually exclusive with
// `PartialIndicator` (text streaming) — the moment text starts forming
// the parent component swaps this out. See ConversationList.tsx for
// the dispatch.
//
// Founder-first compliance:
// - §3 humanised — `label` is the same string `ToolCallRow` would show
//   for the persisted tool_use, derived from `tool_use.input.description`
//   with the verb-map fallback (computed backend-side in `headline.ts`).
// - §5 actor + verb + state — copy is "Thinking · 0:08" or
//   "Working · 0:14 · <label>". No raw bash, no raw JSON, no token count.
// - §6 zero quantitative metric — only the elapsed wall-clock timer.

import { useEffect, useState } from 'react';
import { Icon, type IconName } from './atoms/Icon';
import { fallbackHeadline } from '../lib/tool-rendering/verb-map';
import { useTranslation } from '../i18n';
import type { LiveActivity } from '../types';

export interface LiveActivityProps {
  activity: LiveActivity;
  // Test seam — defaults to Date.now. Vitest fake timers + a custom now
  // give us deterministic timer assertions without flakes.
  nowMs?: () => number;
}

// Format the wall-clock elapsed value as `0:08` / `12:34` / `1h 02m`.
// Sub-second precision is intentionally dropped: a flicker between 0:00
// and 0:01 would feel fragile, and the founder gains zero from it.
export function formatElapsed(elapsedMs: number): string {
  const totalSec = Math.max(0, Math.floor(elapsedMs / 1000));
  if (totalSec < 3600) {
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

// Resolve a Lucide icon name from a tool's running label. The tracker
// only ships the rendered `label` (not the raw tool name), so we fall
// back to a generic `Wrench` if the label happens to start with one of
// the verb-map prefixes — purely cosmetic, no founder-facing impact.
function iconForToolLabel(label: string): IconName {
  // Cheap prefix lookup for the seven common cases. Anything unknown
  // gets the generic Wrench icon — same as the row would show.
  if (label.startsWith('Opened ')) return 'BookOpen';
  if (label.startsWith('Edited ')) return 'FileEdit';
  if (label.startsWith('Wrote ')) return 'FilePlus';
  if (label.startsWith('Listed ')) return 'Folder';
  if (label.startsWith('Searched the codebase') || label.startsWith('Searched for ')) return 'Search';
  if (label.startsWith('Searched the web') || label.startsWith('Fetched ')) return 'Globe';
  if (label.startsWith('Ran subagent')) return 'Bot';
  if (label.startsWith('Updated plan')) return 'ListChecks';
  if (label.startsWith('Edited a notebook') || label.startsWith('Edited ') && label.endsWith('.ipynb')) return 'Notebook';
  if (label.startsWith('Invoked a skill')) return 'Sparkles';
  if (label.startsWith('Sent a notification')) return 'Bell';
  // Reference: keep `fallbackHeadline` import alive so a future tool
  // registry change forces a typecheck failure here too. Cheap reminder.
  void fallbackHeadline;
  return 'Wrench';
}

function PulsingDots({ ariaLabel }: { ariaLabel: string }) {
  // Sprint 17 — three semantic utilities (`animate-pulse-dot-1|2|3`)
  // bake the 200ms stagger into the Tailwind animation declaration so
  // the per-dot delay is a token, not an inline style. See
  // `docs/ui/04-tokens.md` §6.1 and `docs/design-system.md` §11.4.
  return (
    <span
      className="inline-flex items-center gap-0.5"
      role="status"
      aria-label={ariaLabel}
      data-testid="live-activity-dots"
    >
      <span
        className="inline-block w-1 h-1 rounded-full bg-muted-foreground animate-pulse-dot-1"
        aria-hidden="true"
      />
      <span
        className="inline-block w-1 h-1 rounded-full bg-muted-foreground animate-pulse-dot-2"
        aria-hidden="true"
      />
      <span
        className="inline-block w-1 h-1 rounded-full bg-muted-foreground animate-pulse-dot-3"
        aria-hidden="true"
      />
    </span>
  );
}

export function LiveActivity({ activity, nowMs }: LiveActivityProps) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => (nowMs ? nowMs() : Date.now()));

  // 1Hz tick. One interval shared per mount; the parent ensures only
  // one LiveActivity is mounted at a time.
  useEffect(() => {
    const id = setInterval(() => {
      setNow(nowMs ? nowMs() : Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, [nowMs]);

  const elapsed = formatElapsed(Math.max(0, now - activity.startedAtMs));

  // Sprint ai-elements-rendering (Phase 2, T+X) — restyled as the "active"
  // ChainOfThoughtStep of the work rail (ADR D-026): foreground tone +
  // entrance animation, matching chain-of-thought.tsx `statusStyles.active`.
  // Everything else (dots, icon lookup, 1Hz tick, aria, copy) unchanged.
  if (activity.kind === 'thinking') {
    return (
      <div
        className="fade-in-0 slide-in-from-top-2 animate-in flex justify-start py-1"
        data-testid="live-activity"
        data-kind="thinking"
        aria-live="polite"
      >
        <span className="inline-flex items-center gap-2 text-caption text-foreground">
          <PulsingDots ariaLabel={t('liveActivity.ariaLabel')} />
          <span>
            {t('liveActivity.thinking')} · {elapsed}
          </span>
        </span>
      </div>
    );
  }

  // kind === 'tool'
  return (
    <div
      className="fade-in-0 slide-in-from-top-2 animate-in flex justify-start py-1"
      data-testid="live-activity"
      data-kind="tool"
      aria-live="polite"
    >
      {/* max-w-full + min-w-0: el tope de 60ch es MAYOR que un movil (390px);
          sin el tope del contenedor, el chip se salia de la pantalla. */}
      <span className="inline-flex max-w-full items-center gap-2 text-caption text-foreground">
        <Icon
          name={iconForToolLabel(activity.label)}
          size={14}
          className="shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
        <span className="min-w-0 truncate max-w-live-activity">
          {t('liveActivity.working')} · {elapsed} · {activity.label}
        </span>
      </span>
    </div>
  );
}
